import React, { useState, useEffect } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { useParentDashboardContextContext } from '../../../context/ParentDashboardContext';
import { useSafeTranslation } from '../../../hooks/useSafeTranslation';
import { useThemeContext } from '../../../context/ThemeProvider';
import CustomChakraButton from '../../CustomChakraButton/CustomChakraButton';
import Navigation from '../../Navigation/Navigation';
import CreateNewThread from './CreateNewThread/CreateNewThread';
import { useApi } from '../../../context/ApiProvider';
import { ParentMessagePageComponent } from './ParentMessagePageComponent/ParentMessagePageComponent';

const ParentMailBox = () => {
  const { t } = useSafeTranslation();
  const { parentDashboardContext } = useParentDashboardContextContext();
  const { setBasketStatus } = useParentDashboardContextContext();
  const { apiParentMessageController } = useApi();
  const { isMobile } = useThemeContext();
  const location = useLocation();

  const [chosenTenantId, setChosenTenantId] = useState<number>();
  const [showPopUp, setShowPopUp] = useState<boolean>(false);
  const [refreshMessages, setRefreshMessages] = useState<() => void>(() => () => {});

  useEffect(() => {
    if (parentDashboardContext) {
      setChosenTenantId(parentDashboardContext.tenants[0].tenantId);
    }
  }, [parentDashboardContext]);

  useEffect(() => {
    setBasketStatus(() => ({ show: false, forceShow: false }));
  }, []);

  const fetchMessagesFn = async ({
    tenantId,
    pageNumber,
    pageSize
  }: {
    tenantId: string;
    pageNumber: number;
    pageSize: number;
  }) => {
    const response = await apiParentMessageController('thread-list').get(`/${tenantId}`, {
      params: { pageNumber, pageSize }
    });

    const { data, next, hasNextPage, totalCount } = response.data;
    return { data, next, hasNextPage, totalCount };
  };

  const deleteMessageFn = async (threadId: number) => {
    await apiParentMessageController('thread').delete(`/${threadId}`);
  };

  const fetchAnnouncementsFn = async ({
    pageNumber,
    pageSize
  }: {
    tenantId: string;
    pageNumber: number;
    pageSize: number;
  }) => {
    const response = await apiParentMessageController('announcement-list').get(``, {
      params: { pageNumber, pageSize }
    });

    const { data, next, hasNextPage, totalCount } = response.data;
    return { data, next, hasNextPage, totalCount };
  };

  const navOptions = [
    {
      link: 'messages',
      name: (
        <>
          {t('parent_mailbox.messages')}{' '}
          {!!parentDashboardContext && parentDashboardContext.tenants[0].unreadThreads > 0 && (
            <span className="ml-2 mb-1.5 text-xs bg-brand-600 text-white rounded-full px-1.5 py-0.5">
              {parentDashboardContext.tenants[0].unreadThreads}
            </span>
          )}
        </>
      ),
      component: (
        <ParentMessagePageComponent
          tenantId={chosenTenantId?.toString() || ''}
          setRefreshMessages={setRefreshMessages}
          fetchMessagesFn={fetchMessagesFn}
          deleteMessageFn={deleteMessageFn}
        />
      )
    },
    {
      link: 'announcements',
      name: (
        <>
          {t('parent_mailbox.announcements')}{' '}
          {!!parentDashboardContext && parentDashboardContext.tenants[0].unreadAnnouncements > 0 && (
            <span className="ml-2 mb-1.5 text-xs bg-brand-600 text-white rounded-full px-1.5 py-0.5">
              {parentDashboardContext.tenants[0].unreadAnnouncements}
            </span>
          )}
        </>
      ),
      component: (
        <ParentMessagePageComponent
          tenantId={chosenTenantId?.toString() || ''}
          setRefreshMessages={setRefreshMessages}
          fetchMessagesFn={fetchAnnouncementsFn}
          deleteMessageFn={deleteMessageFn}
        />
      )
    }
  ];

  return (
    <section className="pt-spacing-3xl pb-12 gap-spacing-3xl">
      <div className="flex justify-between pr-spacing-4xl pl-spacing-3xl">
        <div>
          <h2 className="font-semibold text-grayLight-900 text-xl md:text-3xl">
            {location.pathname.includes('messages')
              ? t('parent_mailbox.messages')
              : t('parent_mailbox.announcements')}
          </h2>
        </div>
        {location.pathname.includes('messages') &&
          (isMobile ? (
            <div className="fixed bottom-28 right-10 lg:flex">
              <CustomChakraButton
                iconPosition="left"
                icon="plus"
                iconColor="#292524"
                hierarchy="primary"
                size="xl"
                buttonProps={{
                  onClick: () => setShowPopUp(true)
                }}>
                {t('parent_mailbox.new')}
              </CustomChakraButton>
            </div>
          ) : (
            <div className="flex">
              <CustomChakraButton
                iconPosition="right"
                icon="plus"
                iconColor="#292524"
                hierarchy="primary"
                size="md"
                buttonProps={{
                  onClick: () => setShowPopUp(true)
                }}>
                {t('parent_mailbox.new_message')}
              </CustomChakraButton>
            </div>
          ))}
      </div>

      <div className="flex flex-col">
        <div className="pr-spacing-4xl">
          <Navigation navOptions={navOptions} />
        </div>

        <div className="pr-0 md:pr-8 overflow-y-hidden">
          <Routes>
            {navOptions.map((item) => (
              <Route key={item.link} path={`/${item.link}/*`} element={item.component} />
            ))}
          </Routes>
        </div>
      </div>

      {showPopUp && (
        <CreateNewThread
          isOpen={showPopUp}
          onClose={() => {
            refreshMessages();
            setShowPopUp(false);
          }}
        />
      )}
    </section>
  );
};

export default ParentMailBox;
